import { useState } from 'react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/dateFmt';
import {
  CheckCircle2,
  CircleDashed,
  FileText,
  RefreshCcw,
} from 'lucide-react';
import {
  STATIONS,
  type KitIssuedPayload,
  type MobilisationEvent,
  type Station,
} from '@hcc/types';
import { api, ApiError } from '../../lib/api';
import { Button } from '../ui/Button';

/**
 * Per-person summary of every station's signed kit receipt. One row
 * per station with status + View / Regenerate buttons; this is the
 * primary way to get to a receipt outside the audit-history view.
 *
 * Built entirely from the person's event chain — no extra fetch.
 */
export function KitReceiptsPanel({
  personId,
  events,
}: {
  personId: string;
  events: MobilisationEvent[] | null;
}) {
  // Latest KitIssued per station, indexed by station.
  const latestByStation = new Map<
    Station,
    { event_id: string; at: string; item_count: number }
  >();
  if (events) {
    for (const ev of events) {
      if (ev.event_type !== 'KitIssued') continue;
      const p = ev.payload as KitIssuedPayload;
      if (!p.station) continue;
      // Walk chronologically; later issuances overwrite the entry.
      latestByStation.set(p.station, {
        event_id: ev.id,
        at: ev.created_at,
        item_count: p.items?.length ?? 0,
      });
    }
  }

  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <header className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <FileText className="h-4 w-4 text-fg-muted" />
        <h2 className="text-sm font-semibold text-fg">Kit receipts</h2>
        <span className="ml-auto text-xs text-fg-dim">
          Signed PDF per station
        </span>
      </header>
      <ul className="divide-y divide-divider/10">
        {STATIONS.map((s) => {
          const latest = latestByStation.get(s);
          return (
            <li
              key={s}
              className="flex items-center gap-3 px-5 py-3 sm:px-6"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-token-sm ring-1 ring-line/10 bg-surface-2">
                {latest ? (
                  <CheckCircle2 className="h-4 w-4 text-success" />
                ) : (
                  <CircleDashed className="h-4 w-4 text-fg-dim" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-fg">{s}</div>
                <div className="text-[11px] text-fg-muted">
                  {latest ? (
                    <>
                      {formatDateTime(latest.at)}
                      {' · '}
                      {latest.item_count} item
                      {latest.item_count === 1 ? '' : 's'}
                    </>
                  ) : (
                    'No kit issued at this station yet'
                  )}
                </div>
              </div>
              {latest ? (
                <div className="flex items-center gap-1.5">
                  <a
                    href={`/api/admin/kit-receipts/${latest.event_id}/pdf?person_id=${personId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-8 items-center gap-1.5 rounded-token-sm border border-line-strong/20 bg-surface px-2.5 text-xs font-medium text-fg shadow-token-sm transition-colors hover:bg-surface-2"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    View
                  </a>
                  <RegenerateButton personId={personId} eventId={latest.event_id} />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RegenerateButton({
  personId,
  eventId,
}: {
  personId: string;
  eventId: string;
}) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api(`/api/admin/kit-receipts/${eventId}/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ person_id: personId }),
      });
      toast.success('Receipt re-rendered. Click View to open.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'regenerate failed');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      iconLeft={<RefreshCcw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />}
      loading={busy}
      onClick={go}
      title="Re-render this receipt against the current template. Does not re-email."
    >
      Regenerate
    </Button>
  );
}
